import React, { useEffect, useMemo, useState } from "react";
import { Rating, RatingProps } from "semantic-ui-react";
import useUserDataContext from "hooks/useUserDataContext";
import { styledWithTheme } from "styles/theme";
import Typography from "components/atoms/Typography/Typography";
import { definedTypographyTypes } from "components/atoms/Typography/TypographyStyles";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useTheme from "hooks/useTheme";
import { useHistory, useLocation } from "react-router-dom";

import { IRouterState } from "../Modal/LoginModal/LoginModal";

interface Props extends Omit<RatingProps, "maxRating" | "onRate"> {
  mediaSourceId?: string;
}
const StyledDiv = styled.div({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  justifyItems: "center",
  alignItems: "center",
  ".ratingText": {
    fontSize: "0.8rem !important",
  },
  ".mini": {
    i: {
      fontSize: "1.1rem",
    },
  },
});
const StyledRating = styledWithTheme(Rating)(({ theme: stTheme }) => ({
  "&&&&": {
    i: {
      "&.active": {
        color: stTheme.palette.primary.main,
      },
    },
  },
}));

const MediaRating: React.FC<Props> = ({
  mediaSourceId,
  size,
  defaultRating,
  disabled,
  ...props
}) => {
  const theme = useTheme();
  const {
    getRatingFromId,
    updateRatingFromId,
    isLoggedIn,
  } = useUserDataContext();
  const [clicked, setClicked] = useState(false);
  const location = useLocation<IRouterState>();
  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    if (isLoggedIn) {
      history.push(location.pathname, { logInClick: false });
    }
  }, []);

  const onRate = (_: any, data: RatingProps["data"]) => {
    if (!mediaSourceId) return undefined;
    if (!isLoggedIn) {
      history.push(location.pathname, { logInClick: true });
      return undefined;
    }
    updateRatingFromId(mediaSourceId, data.rating);
    setClicked(true);
  };

  const displayedRating = useMemo(
    () =>
      mediaSourceId ? getRatingFromId(mediaSourceId)?.rating : defaultRating,
    [isLoggedIn],
  );

  return (
    <StyledDiv>
      <StyledRating
        {...props}
        disabled={disabled || clicked}
        defaultRating={displayedRating}
        rating={!isLoggedIn ? 0 : undefined}
        maxRating={5}
        size={size || "massive"}
        onRate={onRate}
      />
      {size === "mini" && (
        <Typography
          type={definedTypographyTypes.caption2}
          color={theme.palette.primary.main}
          className="ratingText"
        >
          {t("MediaRating")}
        </Typography>
      )}
    </StyledDiv>
  );
};

export default MediaRating;
